import React, { useState, useEffect } from 'react';
import { Activity, CheckCircle, XCircle, Settings, TrendingUp, AlertCircle, Info } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api`;

export default function ServerSideTracking() {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchConfig();
    fetchStats();
    
    // Refresh stats every 10 seconds
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/tracking/config`);
      const data = await response.json();
      setConfig(data.config);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching config:', error);
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/tracking/stats`);
      const data = await response.json();
      setStats(data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  if (loading) {
    return (
      <div style={{ padding: 40, color: 'white', textAlign: 'center' }}>
        <div style={{ fontSize: 18 }}>Loading tracking configuration...</div>
      </div>
    );
  }
  
  return (
    <div style={{ padding: 40 }}>
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ color: 'white', fontSize: 32, marginBottom: 12, fontWeight: 700 }}>
          Server-Side Tracking
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 16, margin: 0 }}>
          Enhanced conversion tracking across multiple platforms with privacy-first approach
        </p>
      </div>
      
      {/* Info Banner */}
      <div style={{
        background: 'rgba(59,130,246,0.1)',
        border: '1px solid rgba(59,130,246,0.3)',
        borderRadius: 16,
        padding: 20,
        marginBottom: 30,
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start'
      }}>
        <Info size={24} style={{ color: '#3b82f6', flexShrink: 0, marginTop: 2 }} />
        <div>
          <h3 style={{ color: 'white', fontSize: 16, fontWeight: 600, margin: '0 0 8px 0' }}>
            How Server-Side Tracking Works
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
            Events are collected from your frontend, queued on the server, and sent to multiple advertising platforms 
            simultaneously. User data is hashed (SHA-256) for Enhanced Conversions, ensuring privacy compliance while 
            improving match rates by 20-30%. This works alongside your existing client-side tracking.
          </p>
        </div>
      </div>
      
      {/* Platform Status Cards */}
      <h2 style={{ color: 'white', fontSize: 20, fontWeight: 600, marginBottom: 20 }}>
        Platform Status
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 40 }}>
        {config && Object.entries(config).map(([platform, cfg]) => (
          <div key={platform} style={{
            background: 'rgba(255,255,255,0.05)',
            border: `2px solid ${cfg.enabled && cfg.configured ? '#10b981' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 16,
            padding: 24,
            transition: 'all 0.2s ease'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: 'white', margin: 0, fontSize: 18, fontWeight: 600, textTransform: 'capitalize' }}>
                {platform === 'googleAds' ? 'Google Ads' : platform === 'ga4' ? 'GA4' : platform}
              </h3>
              {cfg.enabled && cfg.configured ? (
                <div style={{
                  background: 'rgba(16,185,129,0.2)',
                  borderRadius: 12,
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <CheckCircle size={16} style={{ color: '#10b981' }} />
                  <span style={{ color: '#10b981', fontSize: 12, fontWeight: 600 }}>Active</span>
                </div>
              ) : (
                <div style={{
                  background: 'rgba(239,68,68,0.2)',
                  borderRadius: 12,
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <XCircle size={16} style={{ color: '#ef4444' }} />
                  <span style={{ color: '#ef4444', fontSize: 12, fontWeight: 600 }}>Inactive</span>
                </div>
              )}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.5 }}>
              {cfg.enabled && cfg.configured ? 
                'Configured and ready to track events' : 
                cfg.enabled ? 
                  'Enabled but credentials missing' : 
                  'Disabled - configure in .env to activate'}
            </div>
            {cfg.measurementId && (
              <div style={{ 
                marginTop: 12, 
                padding: '8px 12px', 
                background: 'rgba(255,255,255,0.05)', 
                borderRadius: 8,
                fontSize: 12,
                color: 'rgba(255,255,255,0.5)',
                fontFamily: 'monospace'
              }}>
                {cfg.measurementId}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Statistics */}
      {stats && (
        <>
          <h2 style={{ color: 'white', fontSize: 20, fontWeight: 600, marginBottom: 20 }}>
            Real-Time Statistics
          </h2>
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            borderRadius: 20,
            padding: 30,
            marginBottom: 40
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 30 }}>
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  color: 'rgba(255,255,255,0.6)', 
                  fontSize: 14, 
                  marginBottom: 12,
                  fontWeight: 500
                }}>
                  <TrendingUp size={16} />
                  Queue Length
                </div>
                <div style={{ color: 'white', fontSize: 36, fontWeight: 700 }}>
                  {stats.queueLength}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 4 }}>
                  Events pending
                </div>
              </div>
              
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  color: 'rgba(255,255,255,0.6)', 
                  fontSize: 14, 
                  marginBottom: 12,
                  fontWeight: 500
                }}>
                  <Activity size={16} />
                  Total Events
                </div>
                <div style={{ color: 'white', fontSize: 36, fontWeight: 700 }}>
                  {stats.totalEvents}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 4 }}>
                  All time
                </div>
              </div>
              
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  color: 'rgba(255,255,255,0.6)', 
                  fontSize: 14, 
                  marginBottom: 12,
                  fontWeight: 500
                }}>
                  <CheckCircle size={16} />
                  Active Platforms
                </div>
                <div style={{ color: 'white', fontSize: 36, fontWeight: 700 }}>
                  {Object.values(stats.platforms).filter(Boolean).length}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 4 }}>
                  of 4 configured
                </div>
              </div>
            </div>
            
            {/* Event Breakdown */}
            {Object.keys(stats.eventsByType).length > 0 && (
              <div style={{ marginTop: 30, paddingTop: 30, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
                  Events by Type
                </h3>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {Object.entries(stats.eventsByType).map(([type, count]) => (
                    <div key={type} style={{
                      padding: '8px 16px',
                      background: 'rgba(99,102,241,0.1)',
                      border: '1px solid rgba(99,102,241,0.3)',
                      borderRadius: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <span style={{ color: '#6366f1', fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>
                        {type}
                      </span>
                      <span style={{ color: 'white', fontSize: 14, fontWeight: 700 }}>
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Configuration Guide */}
      <div style={{
        background: 'rgba(255,255,255,0.05)',
        borderRadius: 20,
        padding: 30
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <Settings size={24} style={{ color: '#6366f1' }} />
          <h2 style={{ color: 'white', fontSize: 20, fontWeight: 600, margin: 0 }}>
            Configuration Guide
          </h2>
        </div>
        
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: 1.8 }}>
          <p style={{ marginTop: 0 }}>
            To enable server-side tracking platforms, add the following environment variables to your <code style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: 13,
              color: '#6366f1'
            }}>backend/.env</code> file:
          </p>
          
          <div style={{ marginTop: 20 }}>
            <div style={{ 
              background: 'rgba(0,0,0,0.3)', 
              borderRadius: 12, 
              padding: 20,
              fontFamily: 'monospace',
              fontSize: 13,
              color: '#a5b4fc',
              lineHeight: 1.8,
              overflowX: 'auto'
            }}>
              <div># Google Analytics 4</div>
              <div>GA4_MEASUREMENT_ID=G-XXXXXXXXXX</div>
              <div>GA4_API_SECRET=your_api_secret_here</div>
              <div style={{ marginTop: 12 }}># Google Ads Enhanced Conversions</div>
              <div>GOOGLE_ADS_CONVERSION_ACTION_ID=123456789</div>
              <div style={{ marginTop: 12 }}># Facebook Conversions API</div>
              <div>FACEBOOK_PIXEL_ID=123456789012345</div>
              <div>FACEBOOK_ACCESS_TOKEN=your_access_token_here</div>
              <div style={{ marginTop: 12 }}># LinkedIn Conversions API</div>
              <div>LINKEDIN_CONVERSION_ID=12345678</div>
              <div>LINKEDIN_ACCESS_TOKEN=your_access_token_here</div>
            </div>
          </div>
          
          <div style={{
            marginTop: 20,
            padding: 16,
            background: 'rgba(245,158,11,0.1)',
            border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 12,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start'
          }}>
            <AlertCircle size={20} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600, color: '#f59e0b', marginBottom: 4 }}>
                Important: Server Restart Required
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                After adding environment variables, restart the backend server for changes to take effect.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



